import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, Tooltip, Legend);

/* Colours follow the ENTITY, never its rank: a filter that drops a reason
   must not repaint the others. Reason order/hues validated with the
   dataviz palette checker (adjacent CVD ΔE ≥ 9.1). Aqua and yellow sit
   under 3:1 on white, so every chart using them carries visible labels. */
export const REASON_COLORS = {
  Resigned: "#2a78d6",
  Terminated: "#eb6834",
  "Contract ended": "#1baf7a",
  Absconded: "#eda100",
  "Other / not recorded": "#8a8984",
};
export const REASON_ORDER = Object.keys(REASON_COLORS);

// Male keeps the brand red the gender donut already uses; female is the
// validated amber step (the donut's #ffb500 fails the lightness band).
export const GENDER_COLORS = { male: "#D3001A", female: "#eda100", other: "#8a8984" };

export const SERIES = { primary: "#2a78d6", secondary: "#eb6834", muted: "#c9c8c3" };

export const INK = { primary: "#1F1F1F", secondary: "#52514e", muted: "#6B7280", grid: "#EEF0F2" };

const FONT = { family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", size: 12 };

export const tooltipStyle = {
  backgroundColor: "#ffffff",
  titleColor: INK.primary,
  bodyColor: INK.secondary,
  borderColor: "#E5E7EB",
  borderWidth: 1,
  padding: 10,
  boxPadding: 4,
  titleFont: { ...FONT, weight: "600" },
  bodyFont: FONT,
  usePointStyle: true,
};

const axis = (extra = {}) => ({
  grid: { color: INK.grid, drawTicks: false },
  border: { display: false },
  ticks: { color: INK.muted, font: FONT, padding: 6 },
  ...extra,
});

const valueTicks = (percent) => ({
  color: INK.muted,
  font: FONT,
  padding: 6,
  precision: 0,
  ...(percent ? { callback: (v) => `${v}%` } : {}),
});

/** Shared options: recessive grid, legend only when there is more than one
 *  series, hover on the whole x-slot rather than the thin mark. The value
 *  axis (y, or x when horizontal) starts at zero and carries the gridlines;
 *  the category axis keeps Chart.js's own label callback. */
export const baseOptions = ({ stacked = false, horizontal = false, legend = false, percent = false } = {}) => {
  const valueAxis = axis({ stacked, beginAtZero: true, ticks: valueTicks(percent) });
  const categoryAxis = axis({ stacked, grid: { display: false } });
  return {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? "y" : "x",
    interaction: { mode: "index", intersect: false, axis: horizontal ? "y" : "x" },
    plugins: {
      legend: {
        display: legend,
        position: "bottom",
        labels: { color: INK.secondary, font: FONT, usePointStyle: true, pointStyle: "rectRounded", boxWidth: 10, padding: 14 },
      },
      tooltip: tooltipStyle,
      datalabels: { display: false },
    },
    scales: horizontal ? { x: valueAxis, y: categoryAxis } : { x: categoryAxis, y: valueAxis },
  };
};

/** Thin bars with 4px rounded data-ends and a 2px surface gap. */
export const barDataset = (label, data, color, extra = {}) => ({
  label,
  data,
  backgroundColor: color,
  borderColor: "#ffffff",
  borderWidth: { top: 0, bottom: 0, left: 0, right: 0 },
  borderRadius: 4,
  borderSkipped: "start",
  maxBarThickness: 28,
  categoryPercentage: 0.7,
  barPercentage: 0.9,
  ...extra,
});

/** Value labels at the bar end, in text ink (never the series colour). */
export const endLabels = (formatter = (v) => (v ? v : "")) => ({
  display: true,
  anchor: "end",
  align: "end",
  offset: 2,
  clamp: true,
  color: INK.secondary,
  font: { ...FONT, weight: "600" },
  formatter,
});

/** Value written inside the bar, as the original charts did. White on the
 *  blue/red fills, dark ink on light ones; hidden for empty segments. */
export const insideLabels = (color = "#ffffff") => ({
  display: (ctx) => (ctx.dataset.data[ctx.dataIndex] || 0) > 0,
  anchor: "center",
  align: "center",
  clamp: true,
  color,
  font: { ...FONT, weight: "600", size: 11 },
});

/** Bars never shrink below this, so a count of 1 next to 65 is still a
 *  visible, labelled bar (the original charts used 15px). */
export const MIN_BAR = 18;

/** Distinct colours for categories that each need their own hue
 *  (department tiles). Fixed order, assigned by the category's position in
 *  the name-sorted list, so a department keeps its colour across filters. */
export const CATEGORY_COLORS = [
  "#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300",
  "#4a3aa7", "#e34948", "#0f8b8d", "#8a5a00", "#b03a8f", "#5b6b7a",
];

/** Readable text on a given fill: dark ink on light colours, white otherwise. */
export const textOn = (hex) => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return luminance > 0.3 ? INK.primary : "#ffffff";
};

export const formatMonth = (key) => {
  const [year, month] = key.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleString("en-US", { month: "short", year: "2-digit" });
};

/** Month labels for a trend; the current month is still running, so it is
 *  marked rather than left to read as a sudden drop. */
export const trendLabels = (trend) =>
  trend.map((m, i) => (i === trend.length - 1 ? `${formatMonth(m.month)} (to date)` : formatMonth(m.month)));

export const formatTenure = (months) => {
  if (months === null || months === undefined) return "-";
  if (months < 12) return `${Math.round(months)} mo`;
  return `${(months / 12).toFixed(1)} yr`;
};

export const formatPercent = (value) => (value === null || value === undefined ? "-" : `${value}%`);
